import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useUser } from '../context/UserContext';

const ProductListOne = () => {
    const [flowers, setFlowers] = useState([]);
    const [loading, setLoading] = useState(true);
    const { addToCart } = useUser();

    useEffect(() => {
        const fetchRecentFloweringPlants = async () => {
            try {
                // Fetch only Flowering Plants using the category parameter
                // The route automatically maps 'flowering' to 'Flowering Plants' in database
                const { data } = await axios.get('/api/products', {
                    params: {
                        category: 'flowering', // This maps to 'Flowering Plants' in your route
                        limit: 6, // Only 6 products (one row)
                        page: 1
                    }
                });
                
                if (data.success) {
                    // The API already formats the data, so we can use it directly
                    setFlowers(data.data);
                }
            } catch (error) {
                console.error("Failed to fetch recent flowering plants:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchRecentFloweringPlants();
    }, []);

    // Handle Add to Cart using Global Context
    const handleAddToCart = (e, product) => {
        e.preventDefault();
        e.stopPropagation();
        addToCart(product, 1);
        alert(`${product.name} added to cart!`);
    };

    if (loading) {
        return (
            <div className="product mt-24">
                <div className="container container-lg">
                    <div className="section-heading text-center mb-32">
                        <h4 className="mb-8">Recently Added Flowers</h4>
                    </div>
                    <div className="row gy-4 g-12">
                        {[1, 2, 3, 4, 5, 6].map((item) => (
                            <div key={item} className="col-xxl-2 col-lg-3 col-sm-4 col-6">
                                <div className="product-card px-8 py-16 border border-gray-100 rounded-16">
                                    <div className="skeleton-image" style={{ height: '150px', background: '#f0f0f0', borderRadius: '8px' }}></div>
                                    <div className="mt-12">
                                        <div className="skeleton-text" style={{ height: '20px', width: '80%', marginBottom: '8px', background: '#f0f0f0' }}></div>
                                        <div className="skeleton-text" style={{ height: '16px', width: '60%', background: '#f0f0f0' }}></div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    if (flowers.length === 0) {
        return null;
    }

    return (
        <div className="product mt-24">
            <div className="container container-lg">
                {/* Section Header */}
                

                {/* Single Row - Only 6 flowers max */}
                <div className="row gy-4 g-12">
                    {flowers.map((flower) => (
                        <div key={flower._id} className="col-xxl-2 col-lg-3 col-sm-4 col-6">
                            <div className="product-card px-8 py-16 border border-gray-100 hover-border-main-600 rounded-16 position-relative transition-2">
                                {/* ADD TO CART BUTTON */}
                                <button
                                    onClick={(e) => handleAddToCart(e, flower)}
                                    className="product-card__cart btn bg-main-50 text-main-600 hover-bg-main-600 hover-text-white py-11 px-24 rounded-pill flex-align gap-8 position-absolute inset-block-start-0 inset-inline-end-0 me-16 mt-16"
                                    style={{ border: 'none', cursor: 'pointer', zIndex: 2 }}
                                >
                                    Add <i className="ph ph-shopping-cart" />
                                </button>
                                
                                {/* BADGE (Sale or Hot Deal) */}
                                {flower.badge && (
                                    <div className="position-absolute inset-block-start-0 inset-inline-start-0 ms-16 mt-16">
                                        <span className={`badge ${flower.badgeColor} text-white px-12 py-4 rounded-pill`} style={{ fontSize: '12px' }}>
                                            {flower.badge}
                                        </span>
                                    </div>
                                )}
                                
                                {/* PRODUCT IMAGE */}
                                <Link
                                    to={`/product-details/${flower._id}`}
                                    className="product-card__thumb flex-center"
                                >
                                    <img 
                                        src={flower.image} 
                                        alt={flower.name} 
                                        style={{ width: '100%', height: '150px', objectFit: 'contain' }} 
                                    />
                                </Link>
                                
                                <div className="product-card__content mt-12">
                                    <div className="product-card__price mb-16">
                                        {flower.originalPrice && flower.originalPrice > flower.price && (
                                            <span className="text-gray-400 text-md fw-semibold text-decoration-line-through me-2">
                                                ₹{flower.originalPrice}
                                            </span>
                                        )}
                                        <span className="text-heading text-md fw-semibold">
                                            ₹{flower.price}
                                        </span>
                                        {flower.discountPercentage > 0 && (
                                            <span className="badge bg-danger-600 text-white ms-2 px-8 py-2 rounded-pill" style={{ fontSize: '10px' }}>
                                                {flower.discountPercentage}% OFF
                                            </span>
                                        )}
                                    </div>
                                    <div className="flex-align gap-6">
                                        <span className="text-xs fw-bold text-gray-600">{flower.rating || 0}</span>
                                        <span className="text-15 fw-bold text-warning-600 d-flex">
                                            <i className="ph-fill ph-star" />
                                        </span>
                                        <span className="text-xs fw-bold text-gray-600">({flower.reviewsCount || 0})</span>
                                    </div>
                                    <h6 className="title text-lg fw-semibold mt-12 mb-8">
                                        <Link to={`/product-details/${flower._id}`} className="link text-line-2">
                                            {flower.name}
                                        </Link>
                                    </h6>
                                    <div className="flex-align gap-4">
                                        <span className="text-main-600 text-md d-flex">
                                            <i className="ph-fill ph-storefront" />
                                        </span>
                                        <span className="text-gray-500 text-xs">
                                            By {flower.vendorName || 'Paudhawala Nursery'}
                                        </span>
                                    </div>
                                    <div className="flex-align gap-4 mt-4">
                                        <span className="text-gray-500 text-xs">
                                            Sold: {flower.sold || 0}+
                                        </span>
                                        <span className="text-gray-400">•</span>
                                        <span className="text-gray-500 text-xs">
                                            {flower.unit || 'Plant'}
                                        </span>
                                    </div>
                                    <div className="mt-12">
                                        <div
                                            className="progress w-100 bg-color-three rounded-pill h-4"
                                            role="progressbar"
                                            aria-valuenow={flower.stock}
                                            aria-valuemin={0}
                                            aria-valuemax={100}
                                        >
                                            <div
                                                className={`progress-bar rounded-pill ${flower.stock > 10 ? 'bg-main-600' : 'bg-danger'}`}
                                                style={{ width: `${Math.min((flower.stock / 50) * 100, 100)}%` }}
                                            />
                                        </div>
                                        <span className="text-gray-900 text-xs fw-medium mt-8">
                                            Stock: {flower.stock} left
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default ProductListOne;